import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Plus, Upload, Search, Pencil, Trash2, Leaf, Carrot, FileText, X, ChevronRight,
  Sparkles, AlertTriangle,
} from "lucide-react";
import api, { formatApiError } from "../api";
import {
  Button, Input, Label, Select, Card, Badge, Spinner, EmptyState,
} from "../components/ui/Primitives";
import SmartImportModal from "../components/SmartImportModal";
import UnifiedImportButton from "../components/UnifiedImportButton";
import IngredientMatchSuggestion from "../components/IngredientMatchSuggestion";
import {
  formatEUR, formatDateTime, CATEGORY_LABELS, ALLERGEN_ABBR, parseATDecimal, pricePerUnitBreakdown,
} from "../utils/format";

const UNIT_LABELS = {
  g: "g", kg: "kg", ml: "ml", L: "L", Stueck: "Stück", Portion: "Portion",
};

function AllergenChips({ items = [], compact = false }) {
  const { t } = useTranslation();
  if (!items.length) return <span className="text-ink-muted text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((a) => (
        <span
          key={a}
          title={t(`allergens.${a}`)}
          className={`inline-flex items-center justify-center rounded border border-warn/30 bg-warn/10 text-warn font-medium ${
            compact ? "px-1 py-0 text-[10px] min-w-[22px] h-[18px]" : "px-1.5 py-0.5 text-[10px]"
          }`}
        >
          {compact ? ALLERGEN_ABBR[a] || a.slice(0, 2) : t(`allergens.${a}`)}
        </span>
      ))}
    </div>
  );
}

function IngredientSlideOver({ open, onClose, mode, initial, meta, onSaved }) {
  const { t } = useTranslation();
  const empty = {
    name: "", category: "Sonstiges", unit: "g",
    price_per_unit: "", purchase_quantity: "",
    allergens: [], is_seasonal: false, notes: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [rejectedMatch, setRejectedMatch] = useState(null); // {id, ai_confirmed}

  useEffect(() => {
    if (open) {
      if (mode === "edit" && initial) {
        setForm({
          name: initial.name || "",
          category: initial.category,
          unit: initial.unit,
          price_per_unit: String(initial.price_per_unit ?? "").replace(".", ","),
          purchase_quantity: String(initial.purchase_quantity ?? "").replace(".", ","),
          allergens: initial.allergens || [],
          is_seasonal: !!initial.is_seasonal,
          notes: initial.notes || "",
        });
      } else {
        setForm(empty);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initial]);

  // Phase 30 F2 — Paketpreis = Preis pro Einheit × Einkaufsmenge
  const livePackage = useMemo(() => {
    const ppu = parseATDecimal(form.price_per_unit);
    const qty = parseATDecimal(form.purchase_quantity);
    if (!ppu || !qty) return null;
    return ppu * qty;
  }, [form.price_per_unit, form.purchase_quantity]);
  const livePPU = useMemo(() => parseATDecimal(form.price_per_unit) || null, [form.price_per_unit]);

  const toggleAllergen = (a) => {
    setForm((s) => ({
      ...s,
      allergens: s.allergens.includes(a) ? s.allergens.filter((x) => x !== a) : [...s.allergens, a],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const ppu = parseATDecimal(form.price_per_unit);
      const qty = parseATDecimal(form.purchase_quantity);
      const payload = {
        name: form.name.trim(),
        category: form.category,
        unit: form.unit,
        price_per_unit: ppu,                          // Phase 30 F2 primary input
        purchase_quantity: qty,
        purchase_price: Number((ppu * qty).toFixed(4)), // legacy field; backend recomputes
        allergens: form.allergens,
        is_seasonal: form.is_seasonal,
        notes: form.notes || "",
        ...(rejectedMatch && mode !== "edit" ? {
          matched_against: rejectedMatch.id,
          matched_ai_confirmed: rejectedMatch.ai_confirmed,
        } : {}),
      };
      if (mode === "edit") {
        await api.patch(`/ingredients/${initial.id}`, payload);
        toast.success("Zutat aktualisiert.");
      } else {
        await api.post("/ingredients", payload);
        toast.success("Zutat hinzugefügt.");
      }
      onSaved && onSaved();
      onClose();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" data-testid="ingredient-slideover">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm fade-in" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 w-full sm:w-[520px] bg-bg-surface border-l border-white/10 shadow-modal flex flex-col slide-in">
        <header className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1">
              {mode === "edit" ? "Bearbeiten" : "Neu"}
            </div>
            <h2 className="font-display text-xl font-semibold tracking-tight">
              {mode === "edit" ? t("ingredients.form_edit") : t("ingredients.form_add")}
            </h2>
          </div>
          <button onClick={onClose} className="text-ink-secondary hover:text-ink-primary" data-testid="slideover-close">
            <X size={18} />
          </button>
        </header>

        <form onSubmit={submit} className="flex-1 overflow-y-auto p-6 space-y-5" data-testid="ingredient-form">
          <div>
            <Label htmlFor="ing-name">{t("ingredients.name")}</Label>
            <Input id="ing-name" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} required data-testid="form-name" />
            {mode !== "edit" && (
              <IngredientMatchSuggestion
                name={form.name}
                onAccept={(existing) => {
                  toast.success(`„${existing.name}" wurde übernommen.`);
                  onClose();
                }}
                onTopMatch={(m) => setRejectedMatch(m ? { id: m.id, ai_confirmed: m.ai_confirmed } : null)}
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ing-cat">{t("ingredients.category")}</Label>
              <Select id="ing-cat" value={form.category} onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))} data-testid="form-category">
                {(meta?.categories || []).map((c) => (
                  <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="ing-unit">{t("ingredients.unit")}</Label>
              <Select id="ing-unit" value={form.unit} onChange={(e) => setForm((s) => ({ ...s, unit: e.target.value }))} data-testid="form-unit">
                {(meta?.units || []).map((u) => (
                  <option key={u} value={u}>{UNIT_LABELS[u] || u}</option>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ing-ppu">{t("ingredients.price_per_unit_label", "Preis pro Einheit")} (€)</Label>
              <Input
                id="ing-ppu"
                inputMode="decimal"
                placeholder="24,00"
                value={form.price_per_unit}
                onChange={(e) => setForm((s) => ({ ...s, price_per_unit: e.target.value }))}
                required
                data-testid="form-price-per-unit"
              />
              <div className="text-[10px] text-ink-muted mt-1">
                {t("ingredients.ppu_hint", "Pro")} 1 {UNIT_LABELS[form.unit] || form.unit}
              </div>
            </div>
            <div>
              <Label htmlFor="ing-qty">{t("ingredients.purchase_quantity_label", "Einkaufsmenge")}</Label>
              <Input
                id="ing-qty"
                inputMode="decimal"
                placeholder="0,5"
                value={form.purchase_quantity}
                onChange={(e) => setForm((s) => ({ ...s, purchase_quantity: e.target.value }))}
                required
                data-testid="form-purchase-quantity"
              />
              <div className="text-[10px] text-ink-muted mt-1">
                {t("ingredients.qty_hint", "in")} {UNIT_LABELS[form.unit] || form.unit}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-brand/20 bg-brand/[0.06] px-4 py-3" data-testid="ingredient-ppu-breakdown">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] uppercase tracking-wider text-brand font-medium">
                {t("ingredients.package_price", "Paketpreis")}
              </span>
              {livePackage != null && (
                <span className="num text-brand text-base font-medium" data-testid="form-live-package">
                  = {formatEUR(livePackage, 2)}
                </span>
              )}
            </div>
            {livePPU != null && (
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink-secondary num">
                {pricePerUnitBreakdown(livePPU, form.unit).map((b) => (
                  <span key={b.u}>{formatEUR(b.v, b.digits)} / {b.u}</span>
                ))}
              </div>
            )}
            {livePackage == null && <span className="text-ink-muted text-sm">—</span>}
          </div>

          <div>
            <Label>{t("ingredients.allergens")}</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {(meta?.allergens || []).map((a) => {
                const active = form.allergens.includes(a);
                return (
                  <button
                    type="button"
                    key={a}
                    onClick={() => toggleAllergen(a)}
                    data-testid={`form-allergen-${a}`}
                    className={`px-2 py-1 text-[11px] rounded-md border transition-colors ${
                      active
                        ? "bg-warn/15 border-warn/40 text-warn"
                        : "bg-white/5 border-white/10 text-ink-secondary hover:text-ink-primary hover:border-white/20"
                    }`}
                  >
                    {t(`allergens.${a}`)}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="form-seasonal-label">
              <input
                type="checkbox"
                checked={form.is_seasonal}
                onChange={(e) => setForm((s) => ({ ...s, is_seasonal: e.target.checked }))}
                className="w-4 h-4 rounded border border-white/20 bg-bg-void accent-brand"
                data-testid="form-seasonal"
              />
              <span className="text-sm text-ink-primary flex items-center gap-1.5">
                <Leaf size={14} className="text-ok" /> {t("ingredients.seasonal")}
              </span>
            </label>
          </div>

          <div>
            <Label htmlFor="ing-notes">{t("ingredients.notes")}</Label>
            <textarea
              id="ing-notes"
              value={form.notes}
              onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
              rows={3}
              className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand transition-all resize-none"
              data-testid="form-notes"
            />
          </div>
        </form>

        <footer className="border-t border-white/10 p-4 flex items-center justify-end gap-2 bg-bg-void/50">
          <Button variant="secondary" type="button" onClick={onClose} data-testid="form-cancel">
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="form-save">
            {saving ? t("common.loading") : t("common.save")}
          </Button>
        </footer>
      </aside>
    </div>
  );
}

function PriceHistoryRow({ entry }) {
  const reasonLabel = {
    created: "Erstellt",
    updated: "Aktualisiert",
    csv_import: "CSV Import",
  }[entry.reason] || entry.reason;
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
      <div className="flex items-center gap-3">
        <span className="text-[10px] uppercase tracking-wider text-ink-muted">{reasonLabel}</span>
        <span className="text-xs text-ink-secondary">{formatDateTime(entry.changed_at)}</span>
      </div>
      <div className="num text-sm text-ink-primary">{formatEUR(entry.price_per_unit, 4)}</div>
    </div>
  );
}

function IngredientDetail({ ingredient, onClose }) {
  const { t } = useTranslation();
  if (!ingredient) return null;
  return (
    <div className="fixed inset-0 z-50" data-testid="ingredient-detail">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm fade-in" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 w-full sm:w-[460px] bg-bg-surface border-l border-white/10 shadow-modal flex flex-col slide-in">
        <header className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1">
              {CATEGORY_LABELS[ingredient.category] || ingredient.category}
            </div>
            <h2 className="font-display text-xl font-semibold tracking-tight">{ingredient.name}</h2>
          </div>
          <button onClick={onClose} className="text-ink-secondary hover:text-ink-primary" data-testid="detail-close">
            <X size={18} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="grid grid-cols-2 gap-3">
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">{t("ingredients.purchase_price")}</div>
              <div className="num text-base text-ink-primary">{formatEUR(ingredient.purchase_price)}</div>
            </Card>
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">{t("ingredients.purchase_quantity")}</div>
              <div className="num text-base text-ink-primary">{ingredient.purchase_quantity} {UNIT_LABELS[ingredient.unit] || ingredient.unit}</div>
            </Card>
            <Card className="p-4 col-span-2 border-brand/30 bg-brand/[0.04]">
              <div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("ingredients.price_per_unit")}</div>
              <div className="num text-xl text-brand font-medium">{formatEUR(ingredient.price_per_unit, 4)} / {UNIT_LABELS[ingredient.unit] || ingredient.unit}</div>
            </Card>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">{t("ingredients.allergens")}</div>
            <AllergenChips items={ingredient.allergens} />
          </div>

          {ingredient.notes && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">{t("ingredients.notes")}</div>
              <div className="text-sm text-ink-secondary leading-relaxed whitespace-pre-wrap">{ingredient.notes}</div>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">{t("ingredients.price_history")}</div>
            {ingredient.price_history?.length ? (
              <Card className="p-4">
                {ingredient.price_history.map((h, i) => <PriceHistoryRow key={i} entry={h} />)}
              </Card>
            ) : (
              <div className="text-xs text-ink-muted">{t("ingredients.no_history")}</div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

export default function IngredientsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const initialUnpriced = new URLSearchParams(location.search).get("unpriced") === "1";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [unpricedOnly, setUnpricedOnly] = useState(initialUnpriced);
  const [meta, setMeta] = useState({ categories: [], units: [], allergens: [] });
  const [slideOpen, setSlideOpen] = useState(false);
  const [slideMode, setSlideMode] = useState("add");
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const fileInputRef = useRef(null);
  const [smartImportOpen, setSmartImportOpen] = useState(false);

  const loadMeta = async () => {
    try {
      const { data } = await api.get("/ingredients/meta");
      setMeta(data);
    } catch (e) { /* noop */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (category) params.category = category;
      const { data } = await api.get("/ingredients", { params });
      setItems(data.items);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category]);

  const onDelete = async (id) => {
    if (!window.confirm(t("ingredients.delete_confirm"))) return;
    try {
      await api.delete(`/ingredients/${id}`);
      toast.success("Gelöscht.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openAdd = () => { setSlideMode("add"); setEditing(null); setSlideOpen(true); };
  const openEdit = (it) => { setSlideMode("edit"); setEditing(it); setSlideOpen(true); };

  const filteredItems = useMemo(() => {
    if (!unpricedOnly) return items;
    return items.filter((it) => {
      const ppu = Number(it.price_per_unit);
      return !ppu || ppu <= 0;
    });
  }, [items, unpricedOnly]);

  const unpricedCount = useMemo(
    () => items.filter((it) => !Number(it.price_per_unit) || Number(it.price_per_unit) <= 0).length,
    [items]
  );
  const openDetail = async (it) => {
    try {
      const { data } = await api.get(`/ingredients/${it.id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleCsv = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/ingredients/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("ingredients.imported", { count: data.inserted }));
      if (data.errors?.length) {
        toast.error(`${t("ingredients.errors")}: ${data.errors.length}`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Phase 30 — Excel export
  const exportXlsx = async () => {
    try {
      const res = await api.get("/ingredients/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date();
      const ddmmyyyy = `${String(today.getDate()).padStart(2, "0")}${String(today.getMonth() + 1).padStart(2, "0")}${today.getFullYear()}`;
      a.download = `StudioOps_Zutaten_${ddmmyyyy}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="ingredients-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1.5">Modul · 01</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">{t("ingredients.title")}</h1>
          <p className="text-sm text-ink-secondary mt-1">{t("ingredients.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            data-testid="csv-file-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) { handleCsv(f); e.target.value = ""; }
            }}
          />
          <UnifiedImportButton
            acceptCsvXlsx
            acceptPdf={false}
            onCsvXlsxFile={() => setSmartImportOpen(true)}
            testId="ingredient-import-btn"
          />
          <Button variant="secondary" onClick={exportXlsx} data-testid="ingredients-export-btn">
            <FileText size={14} /> {t("ingredients.export", "Excel-Export")}
          </Button>
          <Button onClick={openAdd} data-testid="add-ingredient-button">
            <Plus size={14} /> {t("ingredients.add")}
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="px-3 py-2.5 mb-4 flex flex-col md:flex-row md:items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("ingredients.search_placeholder")}
            className="!pl-9 !border-transparent !bg-transparent"
            data-testid="search-input"
          />
        </div>
        <div className="hidden md:block w-px h-6 bg-white/10" />
        <div className="flex items-center gap-2 overflow-x-auto py-1">
          <button
            onClick={() => setCategory("")}
            data-testid="category-chip-all"
            className={`px-2.5 py-1 text-[11px] rounded-md border whitespace-nowrap transition-colors ${
              category === "" ? "bg-brand/15 border-brand/40 text-brand" : "bg-transparent border-white/10 text-ink-secondary hover:text-ink-primary hover:border-white/20"
            }`}
          >
            {t("ingredients.all_categories")}
          </button>
          {meta.categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c === category ? "" : c)}
              data-testid={`category-chip-${c}`}
              className={`px-2.5 py-1 text-[11px] rounded-md border whitespace-nowrap transition-colors ${
                category === c ? "bg-brand/15 border-brand/40 text-brand" : "bg-transparent border-white/10 text-ink-secondary hover:text-ink-primary hover:border-white/20"
              }`}
            >
              {CATEGORY_LABELS[c] || c}
            </button>
          ))}
          <div className="hidden md:block w-px h-5 bg-white/10 mx-1" />
          <button
            onClick={() => {
              const next = !unpricedOnly;
              setUnpricedOnly(next);
              const params = new URLSearchParams(location.search);
              if (next) params.set("unpriced", "1"); else params.delete("unpriced");
              navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
            }}
            data-testid="filter-unpriced"
            title="Nur Zutaten ohne lokalen Preis (price_per_unit = 0)"
            className={`px-2.5 py-1 text-[11px] rounded-md border whitespace-nowrap transition-colors inline-flex items-center gap-1.5 ${
              unpricedOnly
                ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
                : "bg-transparent border-white/10 text-ink-secondary hover:text-ink-primary hover:border-white/20"
            }`}
          >
            <AlertTriangle size={11} /> Unbepreist
            {unpricedCount > 0 && (
              <span className="num text-[10px] px-1 rounded bg-amber-500/20 text-amber-200">{unpricedCount}</span>
            )}
          </button>
        </div>
      </Card>

      {/* Table or states */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="py-20 flex items-center justify-center"><Spinner size={20} /></div>
        ) : filteredItems.length === 0 ? (
          <EmptyState
            icon={Carrot}
            title={unpricedOnly ? "Alle Zutaten sind bepreist" : t("ingredients.empty_title")}
            description={unpricedOnly ? "Es gibt keine Zutaten ohne lokalen Preis in diesem Outlet." : t("ingredients.empty_subtitle")}
            action={
              unpricedOnly ? (
                <Button variant="secondary" onClick={() => { setUnpricedOnly(false); navigate({ pathname: location.pathname }, { replace: true }); }} data-testid="clear-unpriced">
                  Filter zurücksetzen
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button onClick={openAdd} data-testid="empty-add-button"><Plus size={14} /> {t("ingredients.add")}</Button>
                  <Button variant="secondary" onClick={() => fileInputRef.current?.click()} data-testid="empty-csv-button">
                    <Upload size={14} /> {t("ingredients.import_csv")}
                  </Button>
                </div>
              )
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ingredients-table">
              <thead>
                <tr className="text-left">
                  {[
                    t("ingredients.name"),
                    t("ingredients.category"),
                    t("ingredients.unit"),
                    t("ingredients.purchase_price"),
                    t("ingredients.purchase_quantity"),
                    t("ingredients.price_per_unit"),
                    t("ingredients.allergens"),
                    "",
                    t("ingredients.last_modified"),
                    "",
                  ].map((h, i) => (
                    <th
                      key={i}
                      className={`text-[10px] uppercase tracking-[0.05em] font-medium text-ink-secondary px-4 py-3 border-b border-white/10 ${
                        i === 5 ? "bg-brand/[0.04]" : ""
                      } ${[3, 4, 5].includes(i) ? "text-right" : ""}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((it, idx) => (
                  <tr
                    key={it.id}
                    onClick={() => openDetail(it)}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors cursor-pointer group"
                    data-testid={`ingredient-row-${idx}`}
                  >
                    <td className="px-4 py-2.5 text-ink-primary font-medium">
                      <div className="flex items-center gap-2">
                        {it.is_seasonal && <Leaf size={12} className="text-ok" />}
                        <span>{it.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge>{CATEGORY_LABELS[it.category] || it.category}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-ink-secondary text-xs">{UNIT_LABELS[it.unit] || it.unit}</td>
                    <td className="px-4 py-2.5 num text-ink-primary text-right">{formatEUR(it.purchase_price)}</td>
                    <td className="px-4 py-2.5 num text-ink-secondary text-right">{it.purchase_quantity}</td>
                    <td className="px-4 py-2.5 num text-brand text-right bg-brand/[0.03] font-medium" data-testid={`row-ppu-${idx}`}>
                      {formatEUR(it.price_per_unit, 4)}
                    </td>
                    <td className="px-4 py-2.5">
                      <AllergenChips items={it.allergens} compact />
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {it.is_seasonal ? <Leaf size={14} className="inline text-ok" /> : <span className="text-ink-muted">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-ink-muted text-xs num">{formatDateTime(it.updated_at)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => { e.stopPropagation(); openEdit(it); }}
                          className="p-1.5 rounded text-ink-secondary hover:text-brand hover:bg-brand/10 transition-colors"
                          aria-label="Edit"
                          data-testid={`row-edit-${idx}`}
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onDelete(it.id); }}
                          className="p-1.5 rounded text-ink-secondary hover:text-err hover:bg-err/10 transition-colors"
                          aria-label="Delete"
                          data-testid={`row-delete-${idx}`}
                        >
                          <Trash2 size={13} />
                        </button>
                        <ChevronRight size={13} className="text-ink-muted ml-1" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* CSV format hint */}
      <div className="mt-3 flex items-center gap-2 text-[11px] text-ink-muted">
        <FileText size={12} /> {t("ingredients.csv_format")}
      </div>

      <IngredientSlideOver
        open={slideOpen}
        onClose={() => setSlideOpen(false)}
        mode={slideMode}
        initial={editing}
        meta={meta}
        onSaved={load}
      />
      <IngredientDetail ingredient={detail} onClose={() => setDetail(null)} />
      {smartImportOpen && (
        <SmartImportModal
          kind="kitchen"
          onClose={() => setSmartImportOpen(false)}
          onDone={() => { setSmartImportOpen(false); load(); }}
        />
      )}
    </div>
  );
}
